import { sfGet, sfPatch, sfPost } from "./salesforceApi";
import {
  fromSalesforceProjectObjective,
  toSalesforceProjectObjective,
} from "../schema/projectObjectiveSchema";
import { getEnv } from "../config/getEnv";

const PO_RESOURCE = getEnv("REACT_APP_SF_PO_RESOURCE") || "/ProjectObjective";

export async function fetchProjectObjective(projectObjectiveId) {
  if (!projectObjectiveId) {
    throw new Error("fetchProjectObjective requires a projectObjectiveId");
  }
  const data = await sfGet(`${PO_RESOURCE}`, {
    id: String(projectObjectiveId),
  });
  const record = Array.isArray(data) ? data[0] : data;
  return fromSalesforceProjectObjective(record || {});
}

export async function fetchProjectObjectivesByProjectId(projectId) {
  if (!projectId) {
    throw new Error("fetchProjectObjectivesByProjectId requires a projectId");
  }
  const data = await sfGet(`${PO_RESOURCE}`, { pid: String(projectId) });
  return Array.isArray(data) ? data : [];
}

export async function fetchAllProjectObjectives() {
  try {
    const data = await sfGet(`${PO_RESOURCE}`, { searchkey: "all" });
    return Array.isArray(data) ? data : [];
  } catch (err) {
    throw err;
  }
}

export async function searchProjectObjectives(searchKey, projectId) {
  const key = String(searchKey || "").trim();
  const pid = String(projectId || "").trim();
  if (key.length === 0) return [];
  const data = await sfGet(`${PO_RESOURCE}`, { searchkey: key, pid: pid });
  return Array.isArray(data) ? data : [];
}

export async function patchProjectObjective(projectObjectiveId, uiRecord) {
  if (!projectObjectiveId)
    throw new Error("patchProjectObjective requires an id");
  const payload = toSalesforceProjectObjective(uiRecord || {});
  return sfPatch(`${PO_RESOURCE}`, payload, {
    params: { id: String(projectObjectiveId) },
  });
}

/**
 * Create a new individual Project Objective
 * @param {Object} uiRecord - UI form data
 * @returns {Promise<Object>} Created project objective response
 */
export async function createIndividualProjectObjective(uiRecord) {
  try {
    console.log(
      "🎯 Creating individual project objective with data:",
      uiRecord,
    );

    // Map UI fields to Salesforce API names
    const payload = {
      // Information fields
      Name: uiRecord.objectiveName || "",
      // Always send Id if available
      Project__c: uiRecord.projectId || uiRecord.project || "",
      Contributor_Facing_Project_Name__c: uiRecord.contributorFacingName || "",
      Append_to_Objective_Name__c: uiRecord.appendToObjectiveName || "",
      Date_Start__c: uiRecord.dateStart || null,
      Date_End__c: uiRecord.dateEnd || null,
      Selection_Criteria__c: uiRecord.selectionCriteria || "",
      Project_Objective_Description__c:
        uiRecord.projectObjectiveDescription || "",

      // Requirements fields
      Country__c: uiRecord.country || "",
      Exclude_States__c: Boolean(uiRecord.excludeStates),
      Currently_Residing_US_State__c: Array.isArray(
        uiRecord.currentlyResidingUsState,
      )
        ? uiRecord.currentlyResidingUsState.join(";")
        : uiRecord.currentlyResidingUsState || "",
      Work_Type__c: uiRecord.workType || "",
      Language__c: uiRecord.language || "",
      Dialect_Country__c: uiRecord.dialect || "",
      Language_Skill_Level__c: uiRecord.languageSkillLevel || "",
      Fluency_Type__c: uiRecord.fluencyType || "",
      Degree_Requirement__c: uiRecord.degreeRequirement || "",
      Additional_Manual_Match_Required__c:
        uiRecord.additionalManualMatchRequired || false,
      Cap_Daily_Hours_Submitted__c: uiRecord.capDailyHoursSubmitted || false,
      Include_in_Country_List__c: uiRecord.includeInCountryList || false,
      List_of_Countries__c: Array.isArray(uiRecord.listOfCountries)
        ? uiRecord.listOfCountries.join(";")
        : uiRecord.listOfCountries || "",

      // Productivity & Quality fields
      Productivity_Target_Type__c: uiRecord.productivityTargetType || "",
      Target_Production_Hours__c: uiRecord.targetProductionHours || null,
      Unit_Pay_Rate__c: uiRecord.unitPayRate || null,
      Main_Quality_Metric__c: uiRecord.mainQualityMetric || "",
      Contributor_Quality_Flag__c: uiRecord.contributorQualityFlag || "",
      Contributor_Productivity_Flag__c:
        uiRecord.contributorProductivityFlag || "",
      Contributor_Overproductivity_Flag__c:
        uiRecord.contributorOverproductivityFlag || "",
      Production_Payment_Threshold__c: uiRecord.productionPaymentThreshold,
      Quality_Payment_Threshold__c: uiRecord.qualityPaymentThreshold,

      to_use_Funnel_A__c: uiRecord.funnelAPercentage,
      to_use_Funnel_B__c: uiRecord.funnelBPercentage,
      to_use_Funnel_C__c: uiRecord.funnelCPercentage,
      to_use_Funnel_D__c: uiRecord.funnelDPercentage,
      to_use_Funnel_E__c: uiRecord.funnelEPercentage,

      // Rates & Payment
      Minimum_Rate__c: uiRecord.minimumRate || null,
      Maximum_Rate__c: uiRecord.maximumRate || null,
      Project_Objective_Pay_Rate__c: uiRecord.projectObjectivePayRate || null,
      Pay_Interval__c: uiRecord.payInterval || "",

      // Action Rules & Flagging
      Days_Between_Reminder_Emails__c:
        uiRecord.daysBetweenReminderEmails || null,
      Email_Content_Complete__c: uiRecord.emailContentComplete || false,

      // Admin fields
      Status__c: uiRecord.status || "",
      Override_DNH__c: uiRecord.overrideDnh || false,
      Create_Invites__c: uiRecord.createInvites || false,
      Display_Lever_Post_Error_Message__c:
        uiRecord.displayLeverPostErrorMessage || false,
      Country_Locale__c: uiRecord.countryLocale || "",
      Language_Locale__c: uiRecord.languageLocale || "",
      Dialect_Locale__c: uiRecord.dialectLocale || "",
      Refresh__c: uiRecord.refresh || false,
    };

    // Remove null/empty values to avoid sending unnecessary data
    Object.keys(payload).forEach((key) => {
      if (
        payload[key] === null ||
        payload[key] === "" ||
        payload[key] === undefined
      ) {
        delete payload[key];
      }
    });

    console.log("📤 Sending payload to Salesforce:", payload);

    const response = await sfPost(`${PO_RESOURCE}`, payload);

    console.log("✅ Project Objective API Response:", {
      type: typeof response,
      isArray: Array.isArray(response),
      hasId: response?.Id || (Array.isArray(response) && response[0]?.Id),
      keys: response ? Object.keys(response).slice(0, 10) : "null",
      fullResponse: response,
    });

    return response;
  } catch (error) {
    console.error("❌ Error creating project objective:", error);
    throw error;
  }
}
